import React from 'react'
import moment from 'moment'
import merge from 'merge'
import { api } from 'core/api/ConnectionManager'
import displaysHelpers from 'core/helpers/_digitalSignages'
import translate from 'core/translate'
import deepEqual from 'fast-deep-equal'
import helpers from 'core/helpers'
import { IDevice } from '../../core/models/DigitalSignage'
import { isEmptyObject } from '../../core/utils/coreUtil'
import { isIpOrDnsAddress } from '../../core/utils/validateUtil'
import store from 'core/store'
import { displaysActions } from 'pages/displays/displays.state'
import { IMonitorSettings } from './__monitorsSettings/MonitorSettings'

interface IDeviceTimer {
    setup: string
    time: string
    repeatDays: Array<string | number>
    date?: string
    rebootType?: string
    useSleepMode?: boolean
    volume?: number
    comPortNumber?: string
}

interface IDeviceTimers {
    on: IDeviceTimer[]
    off: IDeviceTimer[]
}

interface IModBusSetting {
    id: string
    enabledQ: boolean
    deviceIP: string
    TCPPort: string
    scanRate: string
    registers?: string
    startAddr?: string
    registersLength?: string
    floorDelta?: string
    presenceZeroFloor?: boolean
}

interface IProxySettings {
    enabled?: boolean
    ip?: string
    port?: string
    login?: string
    password?: string
    connectionType?: string
}

interface INtpSettings {
    enabled: boolean
    ip: string
    port: string
    dns?: string
}

interface IResizeImageParams {
    backgroundColor: string
    mode: string
    preserveAspectRatioQ: boolean
}

interface IMediaPlayerSettings {
    hardwareAcceleration: string
    options: string[]
}

interface IAndroidMediaPlayerSettings {
    hardwareAcceleration: string
    options?: string[]
}

interface IWebEngineSettings {
    options: string[]
}

interface IBrightnessTimerSettings {
    enabled: boolean
    timers: Array<{ time: string; enabled?: boolean; brightness: number }>
}

export interface IDeviceSettings {
    onOffTimers: IDeviceTimers
    onOffDisplayTimers: IDeviceTimers
    pinCode: string
    displaySettings: Record<string, string | number>
    proxySettings: IProxySettings | null
    speedLimit: string | number | null
    rebootTimers: IDeviceTimer[]
    resizeImageQ: boolean
    resizeImageParams: IResizeImageParams | null
    brightness: Array<{ startTime: string; endTime: string; level: number }>
    brightnessTimers: IBrightnessTimerSettings
    modBus?: IModBusSetting[]
    megafon?: {
        shopCode: string
        tabletMode: string
        inactivityTimeout: number
        prefixWebsite: string
        batteryLevelForShutdown: string | number
    }
    audioOutput: string
    controlsMode: string
    remoteControlEnabled: boolean
    syncContent: boolean
    onlineOfflineMode: 'online' | 'offline'
    hideSystemNotifications: boolean
    contentStoragePath: string
    chequeFilePath: string
    broadcastAutoScreenshot: boolean
    stopBroadcastOnUpdateClientApp?: boolean
    batteryWarningIconDisplay: boolean
    contentDownloadType?: string
    netDiskAddress?: string
    netDiskDefaultMaster?: boolean
    pictureModeList: Array<{ id: string; name: string }>
    pictureMode: string | null
    videoPlayerSettings: IMediaPlayerSettings
    androidVideoPlayerSettings: IAndroidMediaPlayerSettings
    webEngineSettings: IWebEngineSettings
    logLevel: string
    writeSystemLog?: boolean
    audioPlayerSettings: {
        audioPlayerEnabled: string
        showVolumeHandle: string
    }
    browserSettings: {
        openBrowser: string
        flags: string[]
    }
    novastar: {
        login: string
        password: string
    }
    qbicLogin: string | null
    qbicPassword: string | null
    kioskMode: {
        exitCode?: string | null
        invalidCodeLimit?: number | null
        isUseCodeForKioskMode?: boolean | null
    } | null
    p2pDownload: Record<string, unknown>
    timeSynchronization: {
        enabled: boolean
        discrepancy: number
        requestFrequency: number
    }
    ntpSettings?: INtpSettings
    autoScreenshot?: {
        width: number
        height: number
        enabled: boolean
        interval: number
    }
    restartMediaStream: boolean
    useWebRTC: string
    enableIPTV?: boolean
    useChannelGroups: boolean
    changeIpTvVolumeFromRemote?: boolean
    channelGroupId: number[]
    networkStandby: boolean
    refreshScreenAfterWakeupMS: number
    autoSleepTimeAppLevelApi: boolean
    goSleepAfterMS: number
    timeWakeUpEveryDay: string
    screenRefreshTimeEveryDay: string
    exhibitionModeEnabledQ?: boolean
    digitalSignageExhibitionIndex?: number
    ignoreWebPageSecurity?: boolean
    monitorSettings?: IMonitorSettings
    sleepModeSettings?: {
        downloadEnabled?: boolean
        allowManualWake?: boolean
    }
    comPorts?: Array<Record<string, unknown>>
    cameras?: Array<{ name: string; sourceId: string }>
}

interface IPlatform {
    id: number | string
    name: string
}

interface IPlatformSettings {
    platformId: number
    defaultSettings?: Partial<IDeviceSettings>
}

interface IDeviceSettingsSaveData {
    digitalSignageId: number[]
    groupId: number[]
    platforms: Array<{
        name: string
        settings: Partial<IDeviceSettings> | false | null | undefined
    }>
    applyRecursively: boolean
    applyOnlyChangedSettings?: boolean
}

interface ICompanySettings {
    deviceSettings?: {
        contentDownloadType?: {
            values?: string[]
            defaultValue?: string
        }
    }
}

interface IDeviceSettingsOptions {
    devices?: IDevice[]
    groups?: IDevice[]
}

export interface IDeviceSettingsProps {
    settings?: Partial<IDeviceSettings> | IPlatformSettings[]
    deviceSettings?: ICompanySettings
    platform?: string
    platformId?: number
    type?: string
    id?: number
    options?: IDeviceSettingsOptions
    onRequested?: boolean
    emitError: (error: string) => void
    onClose?: () => void
    name?: string
    className?: string | object
    online?: boolean
    small?: boolean
    isConfirm?: boolean
    withoutScroll?: boolean
}

export interface IState {
    settingsList: Record<string, IDeviceSettings>
    settings: IDeviceSettings
    selectedPlatform: IPlatform | null
    availablePlatforms: IPlatform[]
    applyRecursively: boolean
    modalState: boolean
    error: string | null
    isError: boolean
    isMultipleDevicesSettings: boolean
    name?: string
    contentHeight?: number
    platformsForSave: IPlatform[]
    changedSettingsLog: {
        [index: string]: {
            settings: string[]
        }
    }
    applyOnlyChangedSettings: boolean
}

export const AVAILABLE_PLATFORMS: string[] = [
    'windows',
    'linux',
    'android',
    'android_4',
    'sssp',
    'sssp_new',
    'sssp_old',
    'tizen_7',
    'webos',
    'webos_new',
    'webos_old',
    'brightsign',
    'raspberry',
    'colorE-Paper',
]

const makeTimersLoop = () => {
    const on = []
    const off = []
    const obj = {
        setup: 'off',
        time: moment().format('HH:mm:ss'),
        repeatDays: [],
    }

    for (let i = 1; i <= 7; i++) {
        on.push({ ...obj, volume: 75, useSleepMode: true })
        off.push({ ...obj, useSleepMode: true })
    }

    return { on, off }
}

const makeLedTimersLoop = () => {
    const on = []
    const off = []
    const obj = {
        setup: 'off',
        time: moment().format('HH:mm:ss'),
        repeatDays: [],
    }

    for (let i = 1; i <= 7; i++) {
        on.push({ ...obj, comPortNumber: '1' })
        off.push({ ...obj, comPortNumber: '1' })
    }

    return { on, off }
}

const defaultSettings = (settings?: ICompanySettings): IDeviceSettings => {
    const data: Partial<IDeviceSettings> = {}
    const currentTime = moment().format('HH:mm:ss')

    if (helpers.isAvailable({ key: 'megafonSettings', action: 'update' })) {
        data.megafon = {
            shopCode: '',
            tabletMode: 'consultant',
            inactivityTimeout: 10000,
            prefixWebsite: '',
            batteryLevelForShutdown: 0,
        }
    }
    if (helpers.isAvailable({ key: 'modBus', action: 'update' })) {
        data.modBus = [
            {
                id: 'tinkoffLift',
                enabledQ: false,
                deviceIP: '',
                TCPPort: '502',
                registers: '20',
                scanRate: '1000',
                startAddr: '0',
                registersLength: '2',
                floorDelta: '0',
                presenceZeroFloor: false,
            },
            {
                id: 'tinkoffWeather',
                enabledQ: false,
                deviceIP: '',
                TCPPort: '502',
                scanRate: '1000',
            },
        ]
    }
    if (settings?.deviceSettings?.contentDownloadType?.values) {
        data.contentDownloadType = settings.deviceSettings.contentDownloadType.defaultValue
        data.netDiskAddress = ''
        data.netDiskDefaultMaster = false
    }

    return {
        onOffTimers: makeTimersLoop(),
        onOffDisplayTimers: makeLedTimersLoop(),
        pinCode: '',
        displaySettings: {},
        proxySettings: {},
        speedLimit: null,
        rebootTimers: [
            {
                setup: 'off',
                rebootType: 'restartApp',
                time: '14:24:56',
                repeatDays: [],
            },
        ],
        resizeImageQ: false,
        resizeImageParams: null,
        broadcastAutoScreenshot: false,
        remoteControlEnabled: true,
        controlsMode: 'kiosk',
        audioOutput: 'speakers',
        syncContent: false,
        onlineOfflineMode: 'online',
        browserSettings: {
            openBrowser: 'internal',
            flags: [],
        },
        hideSystemNotifications: false,
        contentStoragePath: '',
        chequeFilePath: '',
        pictureModeList: [],
        pictureMode: null,
        videoPlayerSettings: {
            hardwareAcceleration: 'off',
            options: [''],
        },
        androidVideoPlayerSettings: {
            hardwareAcceleration: 'on',
        },
        audioPlayerSettings: {
            audioPlayerEnabled: 'off',
            showVolumeHandle: 'on',
        },
        webEngineSettings: {
            options: [''],
        },
        logLevel: 'Info',
        brightnessTimers: {
            enabled: false,
            timers: [
                {
                    time: moment().format('HH:mm:ss'),
                    brightness: 100,
                },
                {
                    time: moment().format('HH:mm:ss'),
                    brightness: 100,
                },
            ],
        },
        brightness: [
            {
                startTime: moment().format('HH:mm:ss'),
                endTime: moment().format('HH:mm:ss'),
                level: 100,
            },
        ],
        novastar: {
            login: '',
            password: '',
        },
        qbicLogin: null,
        qbicPassword: null,
        kioskMode: {
            exitCode: null,
            invalidCodeLimit: null,
            isUseCodeForKioskMode: null,
        },
        restartMediaStream: false,
        useWebRTC: 'default',
        p2pDownload: {
            enabled: false,
            masterSelect: 'auto',
            masterDeviceId: null,
            enableTls: true,
            engagedEntities: ['broadcastSource', 'advSource', 'distribution'],
            storageMode: 'downloadAndSendAll',
        },
        timeSynchronization: {
            enabled: false,
            discrepancy: 5,
            requestFrequency: 60,
        },
        useChannelGroups: false,
        channelGroupId: [],
        networkStandby: true,
        refreshScreenAfterWakeupMS: 300000,
        autoSleepTimeAppLevelApi: true,
        goSleepAfterMS: 600000,
        timeWakeUpEveryDay: currentTime,
        screenRefreshTimeEveryDay: currentTime,
        batteryWarningIconDisplay: true,
        ...data,
    }
}

const getPlatformName = (platform?: string) => {
    if (!platform) return 'unknown'

    const ssspNew = ['SSSP 4', 'SSSP 5', 'SSSP 6', 'SSSP 7']
    const ssspOld = ['SSSP 2', 'SSSP 3']
    const tizen_7 = ['TIZEN 7', 'SSSP 10']
    const webosNew = ['WEBOS 3.2', 'WEBOS 4', 'WEBOS 4.1', 'WEBOS 6', 'WEBOS 9']
    const webosOld = ['WEBOS 2', 'WEBOS 3']
    const raspberry = ['RASPBERRY_V3 x32', 'RASPBERRY_V3 x64', 'RASPBERRY_V4 x32', 'RASPBERRY_V4 x64']
    const imaqliq: 'IMAQLIQ' = 'IMAQLIQ'
    const redos: 'REDOS' = 'REDOS'
    const android4: 'ANDROID 4' = 'ANDROID 4'

    if (platform === 'ANDROID 4') return 'android_4'

    if (ssspNew.includes(platform)) return 'sssp_new'

    if (ssspOld.includes(platform)) return 'sssp_old'

    if (tizen_7.includes(platform)) return 'tizen_7'

    if (webosNew.includes(platform)) return 'webos_new'

    if (webosOld.includes(platform)) return 'webos_old'

    if (raspberry.includes(platform)) return 'raspberry'

    if (platform.includes(imaqliq) || platform.includes(redos)) return 'linux'

    return displaysHelpers.getPlatformName(platform)
}

const getBrightnessSettings = <T extends { startTime: string; endTime: string }>(brightness: T[]): T[] =>
    brightness.map((timer) => {
        const now = moment().format('YYYY-MM-DD')

        timer.startTime = moment(`${now} ${timer.startTime}`).format('HH:mm:ss')
        timer.endTime = moment(`${now} ${timer.endTime}`).format('HH:mm:ss')

        return timer
    })

class DeviceSettingsMethods extends React.Component<IDeviceSettingsProps, IState> {
    state: IState = {
        settings: defaultSettings(this.props.deviceSettings),
        settingsList: {},
        selectedPlatform: null,
        availablePlatforms: [],
        applyRecursively: true,
        modalState: false,
        error: null,
        isError: false,
        isMultipleDevicesSettings: false,
        platformsForSave: [],
        changedSettingsLog: {},
        applyOnlyChangedSettings: true,
    }

    isError: boolean = false

    componentDidMount() {
        const p_ = this.props
        this.setupSettings(p_)
    }

    componentDidUpdate(p_: Readonly<IDeviceSettingsProps>, prevState: IState) {
        const props = this.props
        const s_ = this.state
        if (props.onRequested && !deepEqual(props, p_)) {
            this.setupSettings(props)
        }

        if (prevState.settings.resizeImageQ !== s_.settings.resizeImageQ && !s_.settings.resizeImageQ) {
            this.setState({
                settings: {
                    ...s_.settings,
                    resizeImageParams: null,
                },
            })
        }
    }

    setupSettings = (p_: IDeviceSettingsProps) => {
        this.setState({
            isMultipleDevicesSettings:
                Array.isArray(p_.options?.devices) || Array.isArray(p_.options?.groups) || p_.type === 'group',
        })

        const settings: Partial<IDeviceSettings> | null =
            p_.settings && !Array.isArray(p_.settings) ? { ...p_.settings } : null

        if (settings && settings.onOffTimers && settings.onOffTimers.on && p_.platform === 'ANDROID') {
            settings.onOffTimers.on.forEach((timer) => {
                if (timer.useSleepMode === undefined) {
                    timer.useSleepMode = true
                }
            })
        }
        if (settings && settings.onOffTimers && settings.onOffTimers.off && p_.platform === 'ANDROID') {
            settings.onOffTimers.off.forEach((timer) => {
                if (timer.useSleepMode === undefined) {
                    timer.useSleepMode = true
                }
            })
        }

        this.getSettings(p_.settings, 1, p_.type)
    }
    getSettings = (
        settings: Partial<IDeviceSettings> | IPlatformSettings[] | undefined,
        platformId: number | string,
        type?: string
    ) => {
        const p_ = this.props
        if (!type) {
            type = p_.type
        }

        if (p_.options?.devices || type === 'group') {
            const data: { digitalSignageId: number[]; groupId: number[] } = {
                digitalSignageId: [],
                groupId: [],
            }

            if (type === 'group') {
                if (p_.id) {
                    data.groupId.push(p_.id)
                }
            } else {
                data.digitalSignageId = (p_.options?.devices || []).map((device) => device.id)
                data.groupId = (p_.options?.groups || []).map((group) => group.id)
            }

            api.send<typeof data, IPlatform[]>('getPlatforms', data).then((res) => {
                this.setPlatformList(res)
            })

            return
        } else {
            if (settings && !Array.isArray(settings) && Object.keys(settings).length > 0) {
                this.mergeOverwriteTimers(merge.recursive(true, this.state.settings, settings) as IDeviceSettings)
            }
        }
    }
    mergeOverwriteTimers = (settings: IDeviceSettings) => {
        const s_ = this.state
        const initTimers = s_.settings.onOffTimers
        const newTimers = settings.onOffTimers
        const timersType: Array<keyof IDeviceTimers> = ['on', 'off']

        if (newTimers) {
            timersType.forEach((type) => {
                initTimers[type].forEach((onTimer, index) => {
                    if (!newTimers[type][index]) {
                        newTimers[type][index] = onTimer
                    }
                })
            })
        }

        const initDisplayTimers = s_.settings.onOffDisplayTimers
        const newDisplayTimers = settings.onOffDisplayTimers
        const timersDisplayType: Array<keyof IDeviceTimers> = ['on', 'off']

        if (newDisplayTimers) {
            timersDisplayType.forEach((type) => {
                initDisplayTimers[type].forEach((onTimer, index) => {
                    if (!newDisplayTimers[type][index]) {
                        newDisplayTimers[type][index] = onTimer
                    }
                })
            })
        }

        let array1 = s_.settings.modBus ? s_.settings.modBus : []
        let array2 = settings.modBus ? settings.modBus : []
        const mergedModBusArray = array1.map((item1) => {
            const item2 = array2.find((item) => item.id === item1.id)
            return item2 ? item2 : item1
        })
        settings.modBus = mergedModBusArray
        this.setState({ settings })
    }

    onSelectPlatform = (selectedPlatform: IPlatform) => {
        const p_ = this.props
        const s_ = this.state

        if (!s_.isMultipleDevicesSettings) {
            this.getSettings(p_.settings, selectedPlatform.id)
            this.setState({ selectedPlatform })
            return
        }

        this.setState({ selectedPlatform, settings: defaultSettings() })
    }

    onCloseAcceptModal = () => {
        const s_ = this.state

        if (!s_.modalState) {
            return
        }

        this.setState({ modalState: false })
    }

    getDeviceSettingsFromCompany = () => {
        const { deviceSettings: settings } = this.props

        if (settings?.deviceSettings?.contentDownloadType?.values) {
            return settings.deviceSettings.contentDownloadType.values
        }

        return null
    }
    getApplyRecursivelyLabel = () => {
        let platform = this.getPlatform()

        if (platform) {
            if (platform === 'sssp_new') {
                platform = 'SSSP 4/5/6/7/10'
            }

            if (platform === 'tizen_7') {
                platform = 'TIZEN 7'
            }

            if (platform === 'sssp_old') {
                platform = 'SSSP 2/3'
            }

            if (platform === 'webos_new') {
                platform = 'WEBOS 4/6'
            }

            if (platform === 'webos_old') {
                platform = 'WEBOS 2/3'
            }

            return `${translate('applyGroupSettingsDisplays')}`
        }
    }
    getPlatform = () => {
        const p_ = this.props
        const { selectedPlatform } = this.state

        let platformName = selectedPlatform ? selectedPlatform.name : p_.platform

        return getPlatformName(platformName)
    }
    getBrightsignSettings = (_settings: IDeviceSettings): Partial<IDeviceSettings> => {
        const data = helpers.deepCopy(_settings)
        const settings: Partial<IDeviceSettings> = {
            rebootTimers: data.rebootTimers,
            logLevel: data.logLevel,
            syncContent: data.syncContent,
            resizeImageQ: data.resizeImageQ,
            enableIPTV: data.enableIPTV,
            changeIpTvVolumeFromRemote: !data.enableIPTV ? false : data.changeIpTvVolumeFromRemote,
            channelGroupId: data.channelGroupId,
            useChannelGroups: data.useChannelGroups,
            resizeImageParams:
                data.resizeImageQ && !data.resizeImageParams
                    ? {
                          backgroundColor: '#00000000',
                          mode: 'inside',
                          preserveAspectRatioQ: true,
                      }
                    : data.resizeImageParams,
        }

        if (data.hasOwnProperty('autoScreenshot')) {
            settings.autoScreenshot = data.autoScreenshot
        }

        return settings
    }
    getFormattedProxy = (proxy?: IProxySettings | null): IProxySettings | null => {
        if (proxy) {
            if (proxy.enabled) {
                return proxy
            }

            return {
                ip: '',
                port: '',
                login: '',
                enabled: false,
                password: '',
                connectionType: '',
            }
        }

        return {}
    }
    getWebOsSettings = (_settings: IDeviceSettings): Partial<IDeviceSettings> => {
        const data = helpers.deepCopy(_settings)
        const settings: Partial<IDeviceSettings> = {
            pinCode: data.pinCode,
            onOffTimers: data.onOffTimers,
            remoteControlEnabled: data.remoteControlEnabled,
            logLevel: data.logLevel,
            syncContent: data.syncContent,
            rebootTimers: data.rebootTimers,
            pictureMode: data.pictureMode || null,
            contentDownloadType: data.contentDownloadType || 'server',
            brightness: getBrightnessSettings(data.brightness),
            audioOutput: data.audioOutput,
            ntpSettings: data.ntpSettings,
            proxySettings: this.getFormattedProxy(data.proxySettings),
            controlsMode: data.controlsMode,
            exhibitionModeEnabledQ: data.exhibitionModeEnabledQ,
            digitalSignageExhibitionIndex: data.digitalSignageExhibitionIndex,
            resizeImageQ: data.resizeImageQ,
            resizeImageParams:
                data.resizeImageQ && !data.resizeImageParams
                    ? {
                          backgroundColor: '#00000000',
                          mode: 'inside',
                          preserveAspectRatioQ: true,
                      }
                    : data.resizeImageParams,
            onOffDisplayTimers: data.onOffDisplayTimers,
            enableIPTV: data.enableIPTV,
            useChannelGroups: data.useChannelGroups,
            channelGroupId: data.channelGroupId,
            changeIpTvVolumeFromRemote: !data.enableIPTV ? false : data.changeIpTvVolumeFromRemote,
            restartMediaStream: data.restartMediaStream,
        }

        if (data.hasOwnProperty('autoScreenshot')) {
            settings.autoScreenshot = data.autoScreenshot
        }

        return settings
    }
    getAndroidSettings = (_settings: IDeviceSettings): Partial<IDeviceSettings> => {
        const data = helpers.deepCopy(_settings)
        const settings: Partial<IDeviceSettings> = {
            onOffTimers: data.onOffTimers,
            controlsMode: data.controlsMode,
            remoteControlEnabled: data.remoteControlEnabled,
            syncContent: data.syncContent,
            logLevel: data.logLevel,
            writeSystemLog: data.writeSystemLog,
            novastar: { ...data.novastar },
            rebootTimers: data.rebootTimers,
            brightness: getBrightnessSettings(data.brightness),
            resizeImageQ: data.resizeImageQ,
            resizeImageParams:
                data.resizeImageQ && !data.resizeImageParams
                    ? {
                          backgroundColor: '#00000000',
                          mode: 'inside',
                          preserveAspectRatioQ: true,
                      }
                    : data.resizeImageParams,
            displaySettings: data.displaySettings,
            enableIPTV: data.enableIPTV,
            useChannelGroups: data.useChannelGroups,
            changeIpTvVolumeFromRemote: !data.enableIPTV ? false : data.changeIpTvVolumeFromRemote,
            audioPlayerSettings: {
                audioPlayerEnabled: data.audioPlayerSettings.audioPlayerEnabled,
                showVolumeHandle: data.audioPlayerSettings.showVolumeHandle,
            },
            browserSettings: {
                openBrowser: data.browserSettings.openBrowser,
            } as IDeviceSettings['browserSettings'],
            androidVideoPlayerSettings: data.androidVideoPlayerSettings,
            speedLimit: data.speedLimit,
            stopBroadcastOnUpdateClientApp: data.stopBroadcastOnUpdateClientApp,
            ignoreWebPageSecurity: data.ignoreWebPageSecurity,
            qbicLogin: data.qbicLogin,
            qbicPassword: data.qbicPassword,
            kioskMode: data.kioskMode ? data.kioskMode : { isUseCodeForKioskMode: false },
            restartMediaStream: data.restartMediaStream,
            useWebRTC: data.useWebRTC || 'default',
            p2pDownload: data.p2pDownload,
            timeSynchronization: data.timeSynchronization,
            sleepModeSettings: {
                downloadEnabled: data.sleepModeSettings?.downloadEnabled ?? false,
                allowManualWake: data.sleepModeSettings?.allowManualWake ?? true,
            },
            channelGroupId: data.channelGroupId,
        }

        if (data.megafon) {
            settings.megafon = data.megafon
            if (!data.megafon.inactivityTimeout) {
                data.megafon.inactivityTimeout = 0
            }
        }

        if (data.hasOwnProperty('autoScreenshot')) {
            settings.autoScreenshot = data.autoScreenshot
        }

        return settings
    }
    getSsspSettings = (_settings: IDeviceSettings): Partial<IDeviceSettings> => {
        const s_ = _settings

        return {
            rebootTimers: s_.rebootTimers,
            onOffTimers: s_.onOffTimers,
            broadcastAutoScreenshot: s_.broadcastAutoScreenshot,
            brightnessTimers: s_.brightnessTimers,
            remoteControlEnabled: s_.remoteControlEnabled,
            audioOutput: s_.audioOutput,
            syncContent: s_.syncContent,
            onlineOfflineMode: s_.onlineOfflineMode || 'online',
            logLevel: s_.logLevel,
            hideSystemNotifications: s_.hideSystemNotifications,
            proxySettings: this.getFormattedProxy(s_.proxySettings),
            ntpSettings: s_.ntpSettings,
            controlsMode: s_.controlsMode,
            resizeImageQ: s_.resizeImageQ,
            resizeImageParams:
                s_.resizeImageQ && !s_.resizeImageParams
                    ? {
                          backgroundColor: '#00000000',
                          mode: 'inside',
                          preserveAspectRatioQ: true,
                      }
                    : s_.resizeImageParams,
            onOffDisplayTimers: s_.onOffDisplayTimers,
            enableIPTV: s_.enableIPTV,
            changeIpTvVolumeFromRemote: !s_.enableIPTV ? false : s_.changeIpTvVolumeFromRemote,
            useChannelGroups: s_.useChannelGroups,
            channelGroupId: s_.channelGroupId,
        }
    }
    getColorEPaperSettings = (_settings: IDeviceSettings): Partial<IDeviceSettings> => {
        const s_ = _settings

        return {
            rebootTimers: s_.rebootTimers,
            broadcastAutoScreenshot: s_.broadcastAutoScreenshot,
            audioOutput: s_.audioOutput,
            logLevel: s_.logLevel,
            hideSystemNotifications: s_.hideSystemNotifications,
            proxySettings: this.getFormattedProxy(s_.proxySettings),
            ntpSettings: s_.ntpSettings,
            resizeImageQ: s_.resizeImageQ,
            resizeImageParams:
                s_.resizeImageQ && !s_.resizeImageParams
                    ? {
                          backgroundColor: '#00000000',
                          mode: 'inside',
                          preserveAspectRatioQ: true,
                      }
                    : s_.resizeImageParams,
            batteryWarningIconDisplay: s_.batteryWarningIconDisplay,
            networkStandby: s_.networkStandby,
            refreshScreenAfterWakeupMS: s_.refreshScreenAfterWakeupMS < 240000 ? 240000 : s_.refreshScreenAfterWakeupMS,
            autoSleepTimeAppLevelApi: s_.autoSleepTimeAppLevelApi,
            goSleepAfterMS: s_.goSleepAfterMS < 60000 ? 60000 : s_.goSleepAfterMS,
            timeWakeUpEveryDay: s_.timeWakeUpEveryDay,
            screenRefreshTimeEveryDay: s_.screenRefreshTimeEveryDay,
        }
    }
    getPlatforms = () => {
        api.send<Record<string, never>, IPlatform[]>('getPlatforms', {}).then((platforms) => {
            this.setPlatformList(platforms)
        })
    }

    setPlatformList = (platforms: IPlatform[]) => {
        const p_ = this.props
        const formattedPlatforms: IPlatform[] = []
        const _platforms = helpers.deepCopy(platforms)

        _platforms.forEach((platform) => {
            platform.name = getPlatformName(platform.name) || platform.name

            if (AVAILABLE_PLATFORMS.includes(platform.name)) {
                const platformExist = formattedPlatforms.filter((formatPlat) => {
                    const item = formatPlat.name.toLowerCase()
                    const repeatPlatforms = ['sssp_new', 'sssp_old', 'windows', 'linux', 'webos_new', 'webos_old']

                    if (repeatPlatforms.includes(item)) {
                        return false
                    }
                    return item === platform.name
                }).length

                if (!platformExist) {
                    formattedPlatforms.push(platform)
                }
            }
        })

        const settingsList: Record<string, IDeviceSettings> = {}

        _platforms.forEach((platform) => {
            const hasSettings =
                Array.isArray(p_.settings) &&
                p_.settings.length > 0 &&
                p_.settings.find((setting) => setting.platformId === platform.id)
            settingsList[platform.name] = hasSettings
                ? { ...defaultSettings(), ...hasSettings.defaultSettings }
                : defaultSettings()
        })

        this.setState({
            availablePlatforms: formattedPlatforms,
            selectedPlatform: formattedPlatforms[0], // selected first item by default
            settingsList,
            platformsForSave: platforms,
        })
    }

    isAvailablePlatform = () => {
        const platform = this.getPlatform()

        return platform ? AVAILABLE_PLATFORMS.includes(platform) : false
    }
    getSaveSettings = (platform: string | undefined, settings: Partial<IDeviceSettings>): Partial<IDeviceSettings> => {
        // TODO: Добавить настройки для windows/linux
        switch (platform) {
            case 'sssp':
            case 'sssp_new':
            case 'sssp_old':
            case 'tizen_7':
                return this.getSsspSettings(settings as IDeviceSettings)
            case 'webos':
            case 'webos_new':
            case 'webos_old':
                return this.getWebOsSettings(settings as IDeviceSettings)
            case 'colorE-Paper':
                return this.getColorEPaperSettings(settings as IDeviceSettings)
            case 'android':
            case 'android_4':
                return this.getAndroidSettings(settings as IDeviceSettings)
            case 'brightsign':
                return this.getBrightsignSettings(settings as IDeviceSettings)
            default:
                return settings
        }
    }

    getConvertedSettings = (platform: string, rawSettings: IDeviceSettings) => {
        let settings: Partial<IDeviceSettings> = helpers.deepCopy(rawSettings)
        const p_ = this.props

        if (platform !== 'windows') {
            // video player settings (vlc)
            delete settings.contentStoragePath
            delete settings.chequeFilePath
        }

        if (platform !== 'android') {
            delete settings.androidVideoPlayerSettings
            delete settings.novastar
            delete settings.qbicLogin
            delete settings.qbicPassword
            delete settings.timeSynchronization
            delete settings.p2pDownload
        }

        if (
            platform !== 'android' &&
            platform !== 'webos_new' &&
            platform !== 'windows' &&
            platform !== 'linux' &&
            platform !== 'raspberry'
        ) {
            delete settings.restartMediaStream
        }

        if (p_.type !== 'group' && settings.p2pDownload) {
            settings.p2pDownload = {
                enabled: Boolean(settings.p2pDownload.enabled),
                engagedEntities:
                    Array.isArray(settings.p2pDownload.engagedEntities) && settings.p2pDownload.engagedEntities.length
                        ? settings.p2pDownload.engagedEntities
                        : ['broadcastSource', 'advSource', 'distribution'],
            }
        }

        if (p_.type === 'group' && settings.p2pDownload) {
            settings.p2pDownload = {
                ...settings.p2pDownload,
                engagedEntities:
                    Array.isArray(settings.p2pDownload.engagedEntities) && settings.p2pDownload.engagedEntities.length
                        ? settings.p2pDownload.engagedEntities
                        : ['broadcastSource', 'advSource', 'distribution'],
                storageMode: settings.p2pDownload.storageMode || 'downloadAndSendAll',
            }
        }

        if (!['windows', 'linux', 'raspberry', 'android'].includes(platform)) {
            delete settings.kioskMode
        } else {
            if (settings.controlsMode === 'system') {
                delete settings.kioskMode
            } else if (!settings.kioskMode?.isUseCodeForKioskMode) {
                delete settings.kioskMode
            } else if (settings.kioskMode?.isUseCodeForKioskMode) {
                settings.kioskMode = {
                    invalidCodeLimit: settings.kioskMode.invalidCodeLimit ?? 3,
                    exitCode: settings.kioskMode.exitCode ?? '95224',
                    isUseCodeForKioskMode: settings.kioskMode.isUseCodeForKioskMode,
                }
            }
        }

        if (platform !== 'windows' && platform !== 'linux' && platform !== 'raspberry') {
            // video player settings (vlc)
            delete settings.videoPlayerSettings
        }

        if (settings.proxySettings && Object.keys(settings.proxySettings).length) {
            const proxy = settings.proxySettings

            if (
                platform !== 'windows' &&
                platform !== 'linux' &&
                platform !== 'sssp_new' &&
                platform !== 'tizen_7' &&
                platform !== 'webos' &&
                platform !== 'webos_old' &&
                platform !== 'webos_new'
            ) {
                delete settings.proxySettings
            } else {
                if (proxy.enabled) {
                    if (
                        !proxy.ip ||
                        !proxy.port ||
                        (!proxy.connectionType && (platform === 'windows' || platform === 'linux'))
                    ) {
                        p_.emitError('emptyProxyFieldsError')
                        this.isError = true

                        return
                    }
                } else {
                    if (!proxy.ip || !proxy.port || !proxy.connectionType) {
                        delete settings.proxySettings
                    }
                }
            }
        } else {
            delete settings.proxySettings
        }

        if (settings.onOffTimers) {
            if (settings.onOffTimers.on && settings.onOffTimers.on.length) {
                settings.onOffTimers.on.forEach((timer) => {
                    if (timer.setup === 'once' && !timer.date) {
                        p_.emitError('noDataInTimer')
                        this.isError = true
                    }
                })
            }
            if (settings.onOffTimers.off && settings.onOffTimers.off.length) {
                settings.onOffTimers.off.forEach((timer) => {
                    if (timer.setup === 'once' && !timer.date) {
                        p_.emitError('noDataInTimer')
                        this.isError = true
                    }
                })
            }
        }

        if (settings.rebootTimers) {
            if (settings.rebootTimers && settings.rebootTimers.length) {
                settings.rebootTimers.forEach((timer) => {
                    if (timer.setup === 'once' && !timer.date) {
                        p_.emitError('noDataInTimer')
                        this.isError = true
                    }
                })
            }
        }

        if (settings.onOffDisplayTimers) {
            if (settings.onOffDisplayTimers.on && settings.onOffDisplayTimers.on.length) {
                settings.onOffDisplayTimers.on.forEach((timer) => {
                    if (timer.setup === 'once' && !timer.date) {
                        p_.emitError('noDataInTimer')
                        this.isError = true
                    }
                })
            }
            if (settings.onOffDisplayTimers.off && settings.onOffDisplayTimers.off.length) {
                settings.onOffDisplayTimers.off.forEach((timer) => {
                    if (timer.setup === 'once' && !timer.date) {
                        p_.emitError('noDataInTimer')
                        this.isError = true
                    }
                })
            }
        }

        if (settings.pinCode?.match(/_/g)) {
            p_.emitError('pinCode')
            return null
        } else {
            let valid = true

            if (platform !== 'windows' && platform !== 'linux' && platform !== 'android') {
                delete settings.displaySettings
                delete settings.contentDownloadType
                delete settings.netDiskAddress
                delete settings.netDiskDefaultMaster
                delete settings.comPorts

                if (
                    platform !== 'android' &&
                    platform !== 'sssp_new' &&
                    platform !== 'tizen_7' &&
                    platform !== 'webos' &&
                    platform !== 'webos_old' &&
                    platform !== 'webos_new' &&
                    platform !== 'raspberry'
                ) {
                    delete settings.controlsMode
                }
                if (platform !== 'raspberry') {
                    delete settings.speedLimit
                }
            } else if ((platform === 'windows' || platform === 'linux') && p_.type === 'group') {
                delete settings.netDiskDefaultMaster
            }

            if (!['sssp_old', 'sssp_new', 'tizen_7', 'sssp'].includes(platform)) {
                delete settings.onlineOfflineMode
            }

            if (settings.contentDownloadType !== 'netDisk') {
                delete settings.netDiskAddress
            }

            if (platform === 'windows' || platform === 'linux') {
                if (settings.megafon) {
                    if (platform === 'windows' || platform === 'linux') {
                        delete settings.megafon.batteryLevelForShutdown

                        if (!settings.megafon.inactivityTimeout) {
                            settings.megafon.inactivityTimeout = 0
                        }
                    }
                }

                if (settings.brightness) {
                    settings.brightness = getBrightnessSettings(settings.brightness)
                }

                const validateFields = ['width', 'height', 'top', 'left']
                delete settings.remoteControlEnabled
                delete settings.hideSystemNotifications

                if (settings.displaySettings && Object.keys(settings.displaySettings).length) {
                    const displaySettings = settings.displaySettings

                    for (let key in displaySettings) {
                        if (displaySettings.hasOwnProperty(key)) {
                            const value = displaySettings[key]

                            if (typeof value === 'string' && value.length) {
                                displaySettings[key] = parseFloat(value)
                            }
                            if (validateFields.includes(key)) {
                                if (typeof displaySettings[key] !== 'number') {
                                    delete displaySettings[key]
                                }
                            }
                        }
                    }

                    if (Object.keys(displaySettings).length) {
                        validateFields.forEach((item) => {
                            if (!displaySettings.hasOwnProperty(item)) {
                                this.setState({ error: item })
                                p_.emitError('coordinatesAreEmpty')
                                valid = false
                            }
                        })
                    } else {
                        delete settings.displaySettings
                    }
                } else {
                    delete settings.displaySettings
                }

                if (settings.speedLimit) {
                    settings.speedLimit = parseInt(String(settings.speedLimit), 10)
                }
            }

            if (!valid) {
                return false
            }

            const sssp: string[] = ['sssp', 'sssp_new', 'sssp_old', 'tizen_7']

            if (platform === 'android' || platform === 'windows' || platform === 'linux' || platform === 'brightsign') {
                delete settings.audioOutput
            }

            if ((platform === 'linux' || platform === 'windows') && settings.onOffTimers) {
                settings.onOffTimers.on = [settings.onOffTimers.on[0]!]
                settings.onOffTimers.off = [settings.onOffTimers.off[0]!]
            } else if (settings.onOffTimers) {
                settings.onOffTimers.on = [...settings.onOffTimers.on]
                settings.onOffTimers.off = [...settings.onOffTimers.off]
            }

            settings = this.getSaveSettings(platform, settings)

            if (
                platform === 'android' &&
                settings.displaySettings &&
                !settings.displaySettings.width &&
                !settings.displaySettings.height
            ) {
                delete settings.displaySettings
            }

            if (!sssp.includes(platform)) {
                delete settings.brightnessTimers
            }

            if (platform === 'sssp_old') {
                delete settings.controlsMode
            }

            if (settings.ntpSettings) {
                const ntp = settings.ntpSettings

                if (ntp.dns) {
                    delete ntp.dns
                }

                if (!ntp.ip) {
                    if (ntp.enabled) {
                        p_.emitError('emptyNtpFieldsError')
                        this.isError = true
                        return
                    }

                    delete settings.ntpSettings
                } else if (ntp.enabled && !isIpOrDnsAddress(ntp.ip)) {
                    p_.emitError('ntpAddressFormatError')
                    this.isError = true
                    return
                }
            }

            if (settings.proxySettings && isEmptyObject(settings.proxySettings)) {
                settings.proxySettings = null
            }

            return settings
        }
    }

    copySetting = <K extends keyof IDeviceSettings>(
        target: Partial<IDeviceSettings>,
        source: Partial<IDeviceSettings>,
        key: K
    ) => {
        target[key] = source[key]
    }

    saveSettings = () => {
        const s_ = this.state
        const p_ = this.props

        const saveData: IDeviceSettingsSaveData = {
            digitalSignageId: [],
            groupId: [],
            platforms: [],
            applyRecursively: s_.applyRecursively,
        }

        if (s_.isMultipleDevicesSettings) {
            s_.platformsForSave.forEach((platform) => {
                const name = platform.name
                const convertedPlatformName: string | undefined = getPlatformName(name)

                if (convertedPlatformName && s_.settingsList[convertedPlatformName]) {
                    let settings = this.getConvertedSettings(
                        convertedPlatformName,
                        s_.settingsList[convertedPlatformName]
                    )

                    if (!settings) {
                        return
                    }

                    if (s_.applyOnlyChangedSettings) {
                        if (!s_.changedSettingsLog[convertedPlatformName]) {
                            return
                        }

                        const formattedSettings: Partial<IDeviceSettings> = {}

                        for (const key of Object.keys(settings) as Array<keyof IDeviceSettings>) {
                            if (s_.changedSettingsLog[convertedPlatformName].settings.includes(key)) {
                                this.copySetting(formattedSettings, settings, key)
                            }
                        }

                        saveData.platforms.push({ name, settings: formattedSettings })

                        return
                    }

                    saveData.platforms.push({ name, settings })
                }
            })
            if (p_.type === 'group') {
                if (p_.id) {
                    saveData.groupId.push(p_.id)
                }
                saveData.applyOnlyChangedSettings = s_.applyOnlyChangedSettings
            } else {
                const devices = p_.options?.devices || []
                const groups = p_.options?.groups || []

                devices.forEach((device) => {
                    if (device.id) {
                        saveData.digitalSignageId.push(device.id)
                    }
                })
                groups.forEach((group) => {
                    if (group.id) {
                        saveData.groupId.push(group.id)
                    }
                })
            }
        } else {
            if (p_.type === 'digitalSignage') {
                const name = p_.platform
                const convertedPlatformName: string | undefined = getPlatformName(name)

                if (name && convertedPlatformName && p_.id) {
                    saveData.digitalSignageId.push(p_.id)
                    saveData.platforms.push({
                        name,
                        settings: this.getConvertedSettings(convertedPlatformName, s_.settings),
                    })
                }
            }
        }

        if (!this.isError) {
            api.send('updateDSAndGroupSettings', saveData).then(() => {
                if (p_.type === 'group' && p_.id) {
                    api.send<{ groupId: number }, IDevice>('getGroup', { groupId: p_.id }).then((res) => {
                        store.dispatch(displaysActions.setUpdatedGroups([res]))
                    })
                }
                if (p_.onClose) {
                    p_.onClose()
                }
            })

            return
        }

        this.isError = false
    }

    //TODO: Удалить после того как все платформы
    // webos_new поддержат эту функциональность. заменить на platform === 'webos_new'
    isRestartMediaStreamAvailable = (platform?: string) => {
        if (!platform) return false
        if (['android', 'tizen_7'].includes(platform)) {
            return true
        }
        const p_ = this.props
        const { selectedPlatform } = this.state
        const platformName = selectedPlatform ? selectedPlatform.name : p_.platform

        return ['WEBOS 6', 'WEBOS 9'].includes(platformName || '')
    }
}

export default DeviceSettingsMethods
